import React, { useEffect, useState, useRef } from "react";
import Plot from "react-plotly.js";
import { Layout } from "plotly.js";
import { useTheme } from "../../theme/useTheme";

interface DataPoint {
  Gene_name: string;
  Shift_to_goal_end: number;
  Goal_end_FDR: number;
  N_Detections: number;
}

interface BubblePlotProps {
  data: DataPoint[];
  highlightedGene?: string;
}

const BubblePlot: React.FC<BubblePlotProps> = ({
  data,
  highlightedGene,
}): JSX.Element => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<any>(null);
  const { mode } = useTheme();

  const handleFullscreen = async () => {
    if (!containerRef.current) return;

    try {
      if (!isFullscreen) {
        if (containerRef.current.requestFullscreen) {
          await containerRef.current.requestFullscreen();
          setIsFullscreen(true);
        }
      } else {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
          setIsFullscreen(false);
        }
      }
    } catch (error) {
      console.error("Fullscreen request failed:", error);
    }
  };

  const handleReset = () => {
    if (plotRef.current?.el) {
      window.Plotly.relayout(plotRef.current.el, {
        "xaxis.autorange": true,
        "yaxis.autorange": true,
      });
    }
  };

  const handleDownloadCSV = () => {
    if (transformedData.length === 0) return;

    // Prepare CSV headers
    const headers = [
      "Gene_name",
      "Shift_to_goal_end",
      "Goal_end_FDR",
      "neg_log10_FDR",
      "N_Detections",
    ];

    // Prepare CSV rows
    const rows = transformedData.map((point) => [
      point.Gene_name,
      point.Shift_to_goal_end.toFixed(6),
      point.Goal_end_FDR.toExponential(2),
      point.neg_log10_FDR.toFixed(2),
      point.N_Detections,
    ]);

    // Combine headers and rows
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "bubble_plot_data.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadChart = () => {
    if (plotRef.current?.el) {
      window.Plotly.downloadImage(plotRef.current.el, {
        format: "png",
        filename: "bubble_plot",
        width: 1200,
        height: 800,
      });
    }
  };
  // Transform FDR values to -log10
  const transformData = (data: DataPoint[]) => {
    return data.map((point) => ({
      ...point,
      // Handle FDR values of 0 by using a very small number (e.g., 1e-300) instead
      neg_log10_FDR: -Math.log10(
        point.Goal_end_FDR === 0 ? 1e-300 : point.Goal_end_FDR,
      ),
    }));
  };

  const [transformedData, setTransformedData] = useState<
    (DataPoint & { neg_log10_FDR: number })[]
  >([]);
  const [xAxisRange, setXAxisRange] = useState<[number, number]>([-0.02, 0.06]);
  const [yAxisRange, setYAxisRange] = useState<[number, number]>([0, 300]);

  useEffect(() => {
    const transformedDataArray = transformData(data);
    setTransformedData(transformedDataArray);

    // Calculate x-axis range dynamically
    const shiftValues = transformedDataArray.map((d) => d.Shift_to_goal_end);
    const minShift = Math.min(...shiftValues);
    const maxShift = Math.max(...shiftValues);
    // Add 10% padding to x-axis for minimal but sufficient space
    const xPadding = (maxShift - minShift) * 0.1;
    setXAxisRange([minShift - xPadding, maxShift + xPadding]);

    // Calculate y-axis range dynamically
    const fdrValues = transformedDataArray.map((d) => d.neg_log10_FDR);
    const maxFDR = Math.max(...fdrValues);
    const minFDR = Math.min(...fdrValues);
    // Add 20% padding to both top and bottom for more vertical space
    const yPadding = (maxFDR - minFDR) * 0.2;
    // Allow negative values in the y-axis range
    setYAxisRange([minFDR - yPadding, maxFDR + yPadding]);
  }, [data]);

  interface CustomPlotData {
    x: (number | null)[];
    y: (number | null)[];
    mode: "markers" | "text+markers";
    type: "scatter";
    marker: {
      size: number[];
      color: (number | string)[];
      symbol?: string | string[];
      colorscale?: "RdBu" | Array<[number, string]>;
      colorbar?: {
        title: {
          text: string;
          side: "right";
        };
      };
      showscale: boolean;
      line: {
        color: string;
        width: number;
      };
      opacity?: number;
      cmin?: number;
      cmax?: number;
    };
    text?: string[];
    hoverinfo: "text" | "none";
    name: string;
    textposition?: "top center";
    showlegend?: boolean;
    textfont?: {
      size?: number;
      family?: string;
      color?: string;
    };
  }

  const getPlotData = (): CustomPlotData[] => {
    // Calculate N_Detections range from actual data
    const minSize = 8; // Reduced minimum bubble size
    const maxSize = 50; // Reduced maximum bubble size
    const nDetectionValues = transformedData.map((d) => d.N_Detections);
    const minDetections = Math.min(...nDetectionValues);
    const maxDetections = Math.max(...nDetectionValues);

    // Calculate x-axis range and color scale values dynamically
    const shiftValues = transformedData.map((d) => d.Shift_to_goal_end);
    const minShift = Math.min(...shiftValues);
    const maxShift = Math.max(...shiftValues);
    // Add 10% padding to the range for x-axis
    const padding = (maxShift - minShift) * 0.1;
    const _xAxisRange = [minShift - padding, maxShift + padding];

    // Calculate quartile breakpoints for color scale (reserved for future styling)
    const range = maxShift - minShift;
    const _q1 = minShift + range * 0.25;
    const _q2 = minShift + range * 0.5;
    const _q3 = minShift + range * 0.75;

    const baseData: CustomPlotData = {
      x: transformedData.map((d) => d.Shift_to_goal_end),
      y: transformedData.map((d) => d.neg_log10_FDR),
      mode: "markers",
      type: "scatter",
      marker: {
        size: transformedData.map((d) => {
          // Use linear scaling for bubble sizes
          const clampedValue = Math.max(
            minDetections,
            Math.min(maxDetections, d.N_Detections),
          );
          const fraction =
            (clampedValue - minDetections) / (maxDetections - minDetections);
          return minSize + fraction * (maxSize - minSize);
        }),
        color: transformedData.map((d) => d.Shift_to_goal_end),
        colorscale: [
          [0, "rgb(255, 180, 100)"], // Lighter orange for lowest values
          [0.33, "rgb(150, 190, 225)"], // Lighter blue for slightly negative
          [0.66, "rgb(100, 180, 140)"], // Lighter green for slightly positive
          [1, "rgb(255, 120, 110)"], // Lighter red for highest values
        ],
        // Set color scale domain to match actual data range
        cmin: minShift, // Dynamic minimum value
        cmax: maxShift, // Dynamic maximum value
        showscale: false,
        line: {
          color: "black", // equivalent to edgecolor="k"
          width: 1,
        },
        opacity: 0.5, // reduced opacity for better visibility
      },
      text: transformedData.map((d) => {
        // Format FDR to readable decimal, showing up to 4 significant digits
        const formattedFDR =
          d.Goal_end_FDR < 0.01
            ? d.Goal_end_FDR.toExponential(2)
            : d.Goal_end_FDR.toPrecision(4);
        return `Gene: ${
          d.Gene_name
        }<br>FDR: ${formattedFDR}<br>Detections: ${d.N_Detections.toLocaleString()}`;
      }),
      hoverinfo: "text",
      name: "All Genes",
      showlegend: false,
    };

    // Create dynamic legend entries for Shift_to_goal_end values
    const numLegendPoints = 4; // Number of legend points we want
    const shiftLegendValues = Array.from(
      { length: numLegendPoints },
      (_, i) => {
        const fraction = i / (numLegendPoints - 1);
        return minShift + (maxShift - minShift) * fraction;
      },
    );
    const legendTraces: CustomPlotData[] = shiftLegendValues.map((value) => ({
      x: [null],
      y: [null],
      mode: "markers",
      type: "scatter",
      marker: {
        size: [12],
        color: [value],
        colorscale: [
          [0, "rgb(255, 180, 100)"], // Lighter orange for lowest values
          [0.33, "rgb(150, 190, 225)"], // Lighter blue for slightly negative
          [0.66, "rgb(100, 180, 140)"], // Lighter green for slightly positive
          [1, "rgb(255, 120, 110)"], // Lighter red for highest values
        ],
        cmin: minShift, // Use the same range as the main plot
        cmax: maxShift,
        showscale: false,
        line: { color: "black", width: 1 },
      },
      name: `${value.toFixed(2)}`,
      legendgroup: "shift_values",
      legendgrouptitle: {
        text: "Shift to goal end",
        font: { color: mode === "dark" ? "#e5e7eb" : "#111827" },
      },
      hoverinfo: "none",
      showlegend: true,
    }));

    // Create dynamic legend entries for N_Detections (bubble sizes)
    // Use the previously calculated min/max N_Detections values
    const numSizeLegendPoints = 4;
    // Create evenly spaced values in log scale for better size distribution in legend
    // Create fixed size increments for legend to ensure visible differences
    const legendSizes = [10, 20, 35, 50]; // Explicitly set sizes for legend markers
    const sizeLegendValues = Array.from(
      { length: numSizeLegendPoints },
      (_, i) => {
        const fraction = i / (numSizeLegendPoints - 1);
        return Math.round(
          minDetections + (maxDetections - minDetections) * fraction,
        );
      },
    );
    const sizeLegendTraces: CustomPlotData[] = sizeLegendValues.map(
      (value, index) => {
        // Use pre-defined sizes for legend markers
        const sizeValue = legendSizes[index];

        return {
          x: [null],
          y: [null],
          mode: "markers",
          type: "scatter",
          marker: {
            size: [sizeValue], // Use the properly scaled size value
            color: ["#808080"],
            showscale: false,
            line: { color: "black", width: 1 },
          },
          name: `${value.toLocaleString()}`,
          legendgroup: "n_detections",
          legendgrouptitle: {
            text: "N Detections",
            font: { color: mode === "dark" ? "#e5e7eb" : "#111827" },
          },
          hoverinfo: "none",
          showlegend: true,
        };
      },
    );

    const plotData = [baseData, ...legendTraces, ...sizeLegendTraces];

    if (highlightedGene) {
      const highlightedPoint = transformedData.find(
        (d) => d.Gene_name === highlightedGene,
      );
      if (highlightedPoint) {
        const highlightData: CustomPlotData = {
          x: [highlightedPoint.Shift_to_goal_end],
          y: [highlightedPoint.neg_log10_FDR],
          mode: "markers",
          type: "scatter",
          marker: {
            size: [24], // Slightly larger for emphasis
            symbol: "star",
            color: ["#FFD700"], // Bright gold/yellow
            showscale: false,
            line: {
              color: "#FFEB3B", // Lighter yellow outline for extra pop
              width: 3,
            },
            opacity: 1,
          },
          text: [highlightedPoint.Gene_name],
          hoverinfo: "text",
          name: "Highlighted Gene",
          showlegend: true,
        };
        plotData.push(highlightData);

        // Add legend entry for FDR reference line
        const fdrLineEntry: CustomPlotData = {
          x: [null],
          y: [null],
          mode: "markers",
          type: "scatter",
          marker: {
            size: [],
            color: [],
            showscale: false,
            line: {
              color: "#9333ea",
              width: 2,
            },
          },
          text: [],
          hoverinfo: "none",
          name: "Highlighted Gene FDR",
          showlegend: true,
        };
        plotData.push(fdrLineEntry as any);
      }
    }

    return plotData;
  };

  const layout: Partial<Layout> = {
    title: {
      text: "Bubble Plot of Candidate Targets",
      font: {
        size: 14,
        color: mode === "dark" ? "#e5e7eb" : "#111827",
      },
    },
    paper_bgcolor: mode === "dark" ? "#1f2937" : "#ffffff",
    plot_bgcolor: mode === "dark" ? "#111827" : "#ffffff",
    xaxis: {
      title: {
        text: "Shift to goal end",
        font: { color: mode === "dark" ? "#e5e7eb" : "#111827" },
      },
      range: xAxisRange, // Dynamic range based on data
      zeroline: true,
      zerolinewidth: 2,
      zerolinecolor: mode === "dark" ? "#6b7280" : "black",
      tickformat: ".2f", // Format tick labels to 2 decimal places
      gridcolor: mode === "dark" ? "#374151" : "#e5e7eb",
      tickfont: { color: mode === "dark" ? "#d1d5db" : "#374151" },
    },
    yaxis: {
      title: {
        text: "-log10(FDR)",
        font: { color: mode === "dark" ? "#e5e7eb" : "#111827" },
      },
      zeroline: false,
      range: yAxisRange, // Dynamic range based on data
      gridcolor: mode === "dark" ? "#374151" : "#e5e7eb",
      tickfont: { color: mode === "dark" ? "#d1d5db" : "#374151" },
    },
    dragmode: "pan",
    shapes: [
      {
        type: "line" as const,
        x0: xAxisRange[0],
        x1: xAxisRange[1],
        y0: -Math.log10(0.05),
        y1: -Math.log10(0.05),
        line: {
          color: mode === "dark" ? "#9ca3af" : "grey",
          width: 1,
          dash: "dash" as const,
        },
      },
      // Add horizontal line for highlighted gene's FDR if exists
      ...(highlightedGene
        ? [
            {
              type: "line" as const,
              x0: xAxisRange[0],
              x1: xAxisRange[1],
              y0:
                transformedData.find((d) => d.Gene_name === highlightedGene)
                  ?.neg_log10_FDR || 0,
              y1:
                transformedData.find((d) => d.Gene_name === highlightedGene)
                  ?.neg_log10_FDR || 0,
              line: {
                color: "#9333ea", // Same purple as the highlighted gene star
                width: 1,
                dash: "dot" as const,
              },
            },
          ]
        : []),
    ],
    annotations:
      highlightedGene &&
      transformedData.find((d) => d.Gene_name === highlightedGene)
        ? [
            {
              x: transformedData.find((d) => d.Gene_name === highlightedGene)
                ?.Shift_to_goal_end,
              y: transformedData.find((d) => d.Gene_name === highlightedGene)
                ?.neg_log10_FDR,
              text: highlightedGene,
              showarrow: false,
              yshift: 30,
              bgcolor: "#FFD700",
              bordercolor: "#FFEB3B",
              borderwidth: 2,
              borderpad: 4,
              font: {
                size: 12,
                color: "#111",
              },
            },
          ]
        : [],
    hovermode: "closest",
    showlegend: true,
    legend: {
      x: 1.02,
      y: 1,
      xanchor: "left",
      yanchor: "top",
      bgcolor:
        mode === "dark" ? "rgba(31, 41, 55, 0.9)" : "rgba(255, 255, 255, 0.9)",
      bordercolor:
        mode === "dark" ? "rgba(156, 163, 175, 0.3)" : "rgba(0, 0, 0, 0.2)",
      borderwidth: 1,
      font: {
        size: 12,
        color: mode === "dark" ? "#e5e7eb" : "#111827",
      },
      itemsizing: "trace", // Changed from 'constant' to allow different sizes
      traceorder: "grouped",
    },
  };

  const plotData = getPlotData();

  // Use a container div to make the plot responsive
  return (
    <div ref={containerRef} className="bubble-plot w-full h-full relative">
      <div className="absolute right-0 z-10 flex gap-2">
        <button
          onClick={handleReset}
          className="p-2 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg shadow-md transition-colors"
          title="Reset zoom"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
        <button
          onClick={handleFullscreen}
          className="p-2 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg shadow-md transition-colors"
          title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        >
          {isFullscreen ? (
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 8V4m0 0h4m-4 0l5 5m11-5v4m0-4h-4m4 0l-5 5M4 20v-4m0 4h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"
              />
            </svg>
          )}
        </button>
        <button
          onClick={handleDownloadCSV}
          className="p-2 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg shadow-md transition-colors"
          title="Download data as CSV"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 16v-4m0 0V8m0 4H8m0 0h4m4 0h4m0 0v4m0-4V8m0 4h-4m0 0l-4 4m0-4l4 4M3 12a9 9 0 1118 0 9 9 0 01-18 0z"
            />
          </svg>
        </button>
        <button
          onClick={handleDownloadChart}
          className="p-2 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg shadow-md transition-colors"
          title="Download chart as PNG"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </button>
      </div>
      <Plot
        ref={plotRef}
        data={plotData as any}
        layout={{
          ...layout,
          autosize: true,
          margin: { l: 60, r: 50, t: 50, b: 60 },
          xaxis: {
            ...layout.xaxis,
            automargin: true,
          },
          yaxis: {
            ...layout.yaxis,
            automargin: true,
          },
        }}
        config={{
          responsive: true,
          displayModeBar: true,
          displaylogo: false,
          modeBarButtons: [[]],
          modeBarButtonsToRemove: [
            "toImage",
            "zoomIn2d",
            "zoomOut2d",
            "resetScale2d",
            "lasso2d",
            "select2d",
            "hoverClosestCartesian",
            "hoverCompareCartesian",
            "sendDataToCloud",
          ],
          toImageButtonOptions: {
            format: "png",
            filename: "bubble_plot",
          },
          scrollZoom: true,
        }}
        className="w-full h-full"
        useResizeHandler={true}
      />
    </div>
  );
};

export { BubblePlot };
